/**
 * Browser-side consent gate, including the hold-and-replay that saves first touches.
 *
 * THE CONSENT-TIMING PROBLEM, and why this file is shaped the way it is.
 *
 * The landing page carrying the UTMs is exactly the page where consent is still
 * undecided. A plugin that simply no-ops until a decision arrives has already lost the
 * campaign parameters by the time the visitor accepts the banner on page two -- and the
 * first touch is the one piece of the path that is irreplaceable. A large fraction of
 * first touches die in that gap.
 *
 * So: before a decision, the parsed touch is held in a JAVASCRIPT VARIABLE and nowhere
 * else. Never a cookie, never sessionStorage, never sent anywhere. In-memory is not
 * storage and is not covered by consent. If consent is granted during the same page view,
 * the held touch is replayed immediately. If the page unloads first, it is gone -- that is
 * accepted, not worked around, because persisting it would be exactly the write consent
 * has not been given for.
 *
 * VERIFIED AGAINST WP Consent API 2.0.1 SOURCE. Its wp_set_consent() dispatches a native
 * CustomEvent on `document` (wp_listen_for_consent_change), and its own README documents
 * listening with document.addEventListener. A native listener in a dependency-free module
 * therefore works, and no jQuery dependency is required. jQuery is still bound to WHEN IT
 * IS ALREADY PRESENT, because jQuery's .trigger() does not dispatch a real DOM event and
 * a consent plugin using that form would otherwise be invisible to us. That binding is
 * belt-and-braces, never a dependency.
 *
 * CookieYes note: its cookieyes_consent_update / cookieyes_banner_loaded events only fire
 * when the WordPress plugin is connected to the CookieYes web app. On a disconnected
 * install they never arrive, which is why WP Consent API is the primary path and the
 * CookieYes path is a documented fallback rather than an equal partner.
 */

export const CONSENT_GRANTED = 'granted';
export const CONSENT_DENIED = 'denied';
export const CONSENT_UNDECIDED = 'undecided';

export const PROVIDER_NONE = 'none';
export const PROVIDER_WP_CONSENT_API = 'wp_consent_api';
export const PROVIDER_COOKIEYES = 'cookieyes';

/**
 * Reads a cookie by exact name from a cookie string.
 *
 * @param {string} name         Cookie name.
 * @param {string} cookieString Raw document.cookie value.
 * @return {string} Cookie value, or an empty string.
 */
function readCookie( name, cookieString ) {
	for ( const pair of String( cookieString || '' ).split( ';' ) ) {
		const index = pair.indexOf( '=' );

		if ( index !== -1 && pair.slice( 0, index ).trim() === name ) {
			return decodeURIComponent( pair.slice( index + 1 ).trim() );
		}
	}

	return '';
}

/**
 * Resolves consent state from the WP Consent API's cookie and consent type.
 *
 * Mirrors wp_has_consent() exactly, including the two cases where an absent cookie still
 * means yes: no consent type set at all, and an optout consent type.
 *
 * @param {object} options              Options.
 * @param {string} options.cookieName   Consent cookie name, e.g. wp_consent_statistics.
 * @param {string} options.cookieString Raw document.cookie value.
 * @param {string} options.consentType  Value of window.wp_consent_type, if any.
 * @return {string} One of the CONSENT_* values.
 */
export function stateFromWpConsentApi( { cookieName, cookieString, consentType } ) {
	const value = readCookie( cookieName, cookieString );
	const type = String( consentType || '' );

	if ( ! type ) {
		// No consent management is driving the API: nothing is withholding consent.
		return CONSENT_GRANTED;
	}

	if ( type.indexOf( 'optout' ) !== -1 && value === '' ) {
		return CONSENT_GRANTED;
	}

	if ( value === 'allow' ) {
		return CONSENT_GRANTED;
	}

	if ( value === 'deny' ) {
		return CONSENT_DENIED;
	}

	return CONSENT_UNDECIDED;
}

/**
 * Resolves consent state from CookieYes's own JavaScript API.
 *
 * getCkyConsent() only exists once the CookieYes banner script has loaded, so its absence
 * means "not yet", not "denied". Feature-detect; never call blind.
 *
 * @param {object} options             Options.
 * @param {object} options.win         Window-like object.
 * @param {string} options.ckyCategory CookieYes category key, e.g. analytics.
 * @return {string} One of the CONSENT_* values.
 */
export function stateFromCookieYes( { win, ckyCategory } ) {
	if ( ! win || typeof win.getCkyConsent !== 'function' ) {
		return CONSENT_UNDECIDED;
	}

	let consent;

	try {
		consent = win.getCkyConsent();
	} catch {
		return CONSENT_UNDECIDED;
	}

	if ( ! consent || typeof consent !== 'object' ) {
		return CONSENT_UNDECIDED;
	}

	if ( ! consent.isUserActionCompleted ) {
		return CONSENT_UNDECIDED;
	}

	const categories = consent.categories || {};

	return categories[ ckyCategory ] ? CONSENT_GRANTED : CONSENT_DENIED;
}

/**
 * Builds the consent gate.
 *
 * @param {object}   [options]              Options.
 * @param {object}   [options.config]       Consent config from PHP (ConsentGate::scriptConfig()).
 * @param {object}   [options.doc]          Document-like object with addEventListener.
 * @param {object}   [options.win]          Window-like object.
 * @param {Function} [options.getCookies]   Returns the raw cookie string.
 * @return {object} Gate.
 */
export function createConsentGate( options = {} ) {
	const config = options.config || {};
	const doc = options.doc;
	const win = options.win || {};
	const getCookies = options.getCookies || ( () => '' );

	const provider = config.provider || PROVIDER_NONE;
	const cookieName = config.cookieName || 'wp_consent_statistics';
	const ckyCategory = config.ckyCategory || 'analytics';

	/**
	 * The touch held in memory while consent is undecided.
	 *
	 * THIS VARIABLE IS THE ENTIRE HOLD MECHANISM. It must never be written to a cookie,
	 * to sessionStorage, to localStorage, or to the network while consent is not granted.
	 *
	 * @type {object|null}
	 */
	let heldTouch = null;

	/** @type {Array<Function>} */
	const grantListeners = [];

	let listening = false;

	/**
	 * Whether the grant listeners have already been notified.
	 *
	 * Consent plugins fire more than one event for a single decision -- WP Consent API
	 * dispatches wp_consent_type_defined and wp_listen_for_consent_change, and CookieYes
	 * adds its own on top. Without this latch one banner click replays the touch three
	 * times, which becomes three REST calls and, past the dedup window, three touches.
	 *
	 * @type {boolean}
	 */
	let notified = false;

	/**
	 * Current consent state.
	 *
	 * @return {string} One of the CONSENT_* values.
	 */
	function state() {
		if ( provider === PROVIDER_NONE ) {
			// No consent management plugin is installed. Matches wp_has_consent().
			return CONSENT_GRANTED;
		}

		if ( provider === PROVIDER_COOKIEYES ) {
			return stateFromCookieYes( { win, ckyCategory } );
		}

		return stateFromWpConsentApi( {
			cookieName,
			cookieString: getCookies(),
			consentType: win.wp_consent_type || win.wp_fallback_consent_type || '',
		} );
	}

	/**
	 * Whether any write of any kind is permitted right now.
	 *
	 * @return {boolean} True when consent is granted.
	 */
	function isGranted() {
		return state() === CONSENT_GRANTED;
	}

	/**
	 * Holds a touch in memory pending a consent decision.
	 *
	 * In-memory only, by design. See the file header.
	 *
	 * @param {object} touch Parsed touch.
	 * @return {void}
	 */
	function hold( touch ) {
		heldTouch = touch || null;
	}

	/**
	 * Returns the held touch without clearing it.
	 *
	 * @return {object|null} Held touch.
	 */
	function held() {
		return heldTouch;
	}

	/**
	 * Returns the held touch and clears it, so a replay cannot fire twice.
	 *
	 * @return {object|null} Held touch.
	 */
	function release() {
		const touch = heldTouch;
		heldTouch = null;
		return touch;
	}

	/**
	 * Discards anything held. Called when consent is refused.
	 *
	 * @return {void}
	 */
	function forget() {
		heldTouch = null;
	}

	/**
	 * Registers a callback for the moment consent becomes granted.
	 *
	 * Fires immediately if consent is already granted, so callers have one code path
	 * rather than two.
	 *
	 * @param {Function} callback Receives the released touch, which may be null.
	 * @return {void}
	 */
	function onGranted( callback ) {
		if ( typeof callback !== 'function' ) {
			return;
		}

		grantListeners.push( callback );

		if ( isGranted() ) {
			notified = true;
			callback( release() );
		}
	}

	/**
	 * Re-evaluates consent and replays the held touch if it is now granted.
	 *
	 * Safe to call as often as any event fires: the release() means a touch is replayed
	 * at most once.
	 *
	 * @return {string} The state that was observed.
	 */
	function reevaluate() {
		const current = state();

		if ( current === CONSENT_GRANTED ) {
			if ( notified ) {
				return current;
			}

			notified = true;

			const touch = release();

			for ( const callback of grantListeners ) {
				callback( touch );
			}
		} else if ( current === CONSENT_DENIED ) {
			// A refusal after an undecided page view: drop what was being held so it
			// cannot be replayed if the visitor later changes their mind mid-page in a
			// way we did not observe.
			forget();
		}

		return current;
	}

	/**
	 * Binds the consent-change events of every supported provider.
	 *
	 * @return {void}
	 */
	function listen() {
		if ( listening || ! doc || typeof doc.addEventListener !== 'function' ) {
			return;
		}

		listening = true;

		const events = [
			// WP Consent API: verified native CustomEvent dispatch in 2.0.1.
			'wp_listen_for_consent_change',
			'wp_consent_type_defined',
			// CookieYes: only fires when the WP plugin is connected to their web app.
			'cookieyes_consent_update',
			'cookieyes_banner_loaded',
		];

		for ( const name of events ) {
			doc.addEventListener( name, reevaluate );
		}

		/*
		 * jQuery's .trigger() invokes jQuery handlers without dispatching a real DOM
		 * event, so a consent plugin using that form is invisible to the listeners above.
		 * WP Consent API 2.0.1 does not need this -- it dispatches natively -- but other
		 * consent plugins implementing the same API might. Bound only when jQuery is
		 * already on the page; never loaded, never depended on.
		 */
		const jq = win.jQuery;

		if ( typeof jq === 'function' ) {
			try {
				jq( doc ).on( events.join( ' ' ), reevaluate );
			} catch {
				// A jQuery too old to accept this call is not a reason to fail the page.
			}
		}
	}

	return {
		state,
		isGranted,
		hold,
		held,
		release,
		forget,
		onGranted,
		reevaluate,
		listen,
		provider: () => provider,
	};
}
